import { useState, useEffect, useContext } from 'react';
import TenantContext from '../Context/TenantContext.jsx';

const SignupAIAgent = () => {
  const { tenant = { name: 'Smart Nurse', branding: { logo: '/logo-bw.png' } } } = useContext(TenantContext) || {};
  const logo = tenant?.branding?.logo || tenant?.logo || '/logo-bw.png';

  const [currentTagline, setCurrentTagline] = useState('');
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);

  const taglines = [
    tenant.tagline || 'AI Enabling Health Systems & Clinics',
    'Smart Healthcare Solutions',
    'Intelligent Patient Care',
    'Advanced Medical Management',
    'Healthcare Innovation Platform',
  ];

  // Typewriter effect
  useEffect(() => {
    const currentFullText = taglines[taglineIndex];
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseTime = 2000;

    const timer = setTimeout(() => {
      if (!isDeleting && charIndex < currentFullText.length) {
        setCurrentTagline(currentFullText.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      } else if (!isDeleting && charIndex === currentFullText.length) {
        setTimeout(() => setIsDeleting(true), pauseTime);
      } else if (isDeleting && charIndex > 0) {
        setCurrentTagline(currentFullText.substring(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      } else if (isDeleting && charIndex === 0) {
        setIsDeleting(false);
        setTaglineIndex((taglineIndex + 1) % taglines.length);
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, taglineIndex, taglines]);

  // Load ElevenLabs widget script
  useEffect(() => {
    const scriptId = 'elevenlabs-convai-embed';
    const existing = document.getElementById(scriptId);
    const onReady = () => setWidgetReady(true);

    if (existing) {
      setTimeout(onReady, 300);
      return undefined;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
    script.async = true;
    script.type = 'text/javascript';
    script.onload = onReady;
    script.onerror = () => setWidgetReady(false);
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  // Force widget open once ready
  useEffect(() => {
    if (!widgetReady) return undefined;

    let attempts = 0;
    const maxAttempts = 12;
    const interval = setInterval(() => {
      attempts += 1;
      const el = document.querySelector('elevenlabs-convai');
      if (!el) return;
      try {
        el.setAttribute('expanded', 'true');
        el.setAttribute('open', 'true');
        el.setAttribute('mode', 'expanded');
        el.setAttribute('data-expanded', 'true');
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        el.expanded = true;
        // @ts-ignore
        el.open = true;
        // @ts-ignore
        if (typeof el.open === 'function') el.open();
        // @ts-ignore
        if (typeof el.expand === 'function') el.expand();
        // @ts-ignore
        const sr = el.shadowRoot;
        if (sr) {
          const openBtn = sr.querySelector('button, [role="button"], .open, .toggle');
          if (openBtn && typeof openBtn.click === 'function') openBtn.click();

          // Strip white backdrop from the top-level shadow wrapper only
          try {
            const sheet = new CSSStyleSheet();
            sheet.replaceSync(`
              :host { background: transparent !important; box-shadow: none !important; }
              :host > * { background: transparent !important; box-shadow: none !important; }
              :host > * > *:first-child { background: transparent !important; }
            `);
            sr.adoptedStyleSheets = [...(sr.adoptedStyleSheets || []), sheet];
          } catch (_) {}
          // Also directly style the first 1-2 wrapper elements in the shadow root
          sr.childNodes.forEach((node) => {
            if (node.nodeType === 1) {
              try {
                node.style.setProperty('background', 'transparent', 'important');
                node.style.setProperty('background-color', 'transparent', 'important');
                node.style.setProperty('box-shadow', 'none', 'important');
              } catch (_) {}
            }
          });
        }
      } catch (_) {
        // Ignore; retries continue
      }
      if (attempts >= maxAttempts) clearInterval(interval);
    }, 250);

    return () => clearInterval(interval);
  }, [widgetReady]);

  // Inject CSS to make widget fill its container natively
  useEffect(() => {
    const styleId = 'elevenlabs-signup-styles';
    if (document.getElementById(styleId)) return undefined;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .elevenlabs-signup-container elevenlabs-convai {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        border: none !important;
        background: transparent !important;
        box-shadow: none !important;
        position: relative !important;
        z-index: 10 !important;
      }
      /* Strip the white host backdrop via exported parts */
      .elevenlabs-signup-container elevenlabs-convai::part(widget),
      .elevenlabs-signup-container elevenlabs-convai::part(container),
      .elevenlabs-signup-container elevenlabs-convai::part(dialog),
      .elevenlabs-signup-container elevenlabs-convai::part(root),
      .elevenlabs-signup-container elevenlabs-convai::part(backdrop),
      .elevenlabs-signup-container elevenlabs-convai::part(overlay) {
        background: transparent !important;
        background-color: transparent !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        margin: 0 !important;
      }
      /* Target the shadow host itself */
      .elevenlabs-signup-container elevenlabs-convai:host,
      .elevenlabs-signup-container elevenlabs-convai:host-context(*) {
        background: transparent !important;
        box-shadow: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existing = document.getElementById(styleId);
      if (existing) existing.remove();
    };
  }, []);

  const agentId =
    import.meta.env.VITE_ELEVENLABS_PATIENT_REGISTRATION_AGENT_ID ||
    'agent_2001knw264rqe8hb3zgdgpm3wx6q';

  return (
    <>
      <style>{`
        @keyframes gradientRotate {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .signup-animated-gradient {
          background: linear-gradient(
            -45deg,
            rgba(59,130,246,0.3),
            rgba(96,165,250,0.4),
            rgba(147,197,253,0.5),
            rgba(191,219,254,0.4),
            rgba(147,197,253,0.5),
            rgba(96,165,250,0.4),
            rgba(59,130,246,0.3)
          );
          background-size: 400% 400%;
          animation: gradientRotate 15s ease infinite;
        }
        .typewriter-cursor {
          display: inline-block;
          width: 2px;
          height: 1.2em;
          background-color: #374151;
          margin-left: 2px;
          animation: blink 1s step-end infinite;
          vertical-align: text-bottom;
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        @keyframes loadingBar {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(150%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>

      <div className="min-h-screen flex items-stretch signup-animated-gradient" style={{ position: 'relative' }}>
        {/* Solid white right-half background — desktop only */}
        <div className="hidden lg:block" style={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', background: 'white', zIndex: 0 }} />
        <div className="max-w-[1430px] mx-auto py-4 px-3 w-full flex items-center" style={{ position: 'relative', zIndex: 1 }}>
          <div className="flex flex-col lg:flex-row w-full items-stretch gap-8">

            {/* Left Panel — login form (admin design) */}
            <div className="w-full lg:w-1/2 flex items-center">
              <div className="w-full max-w-[540px] mx-auto bg-transparent px-4 py-6 lg:px-8 lg:py-8 rounded-2xl">

                {/* Logo — centered + large on mobile, column with typewriter on desktop */}
                <div className="mb-6 lg:mb-8 flex flex-col items-center lg:items-center">
                  <img
                    src={logo}
                    alt={`${tenant.name || 'Smart Nurse'} logo`}
                    className="rounded-lg w-40 lg:w-[260px] mb-0 lg:mb-6"
                    onError={(e) => { e.currentTarget.src = '/logo.png'; }}
                  />
                  <div className="hidden lg:flex min-h-[60px] items-center justify-center">
                    <p className="text-xl font-light text-gray-700 leading-relaxed tracking-wide">
                      {currentTagline}
                      <span className="typewriter-cursor" />
                    </p>
                  </div>
                </div>

                {/* Heading */}
                <div className="mb-0">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-1">Register with AI Agent</h2>
                  <p className="text-sm text-gray-500">
                    Use the voice assistant below to complete your patient registration.
                  </p>
                </div>

                {/* Info cards — desktop only */}
                <div className="hidden lg:block space-y-3">
                  {[
                    { icon: '🎙️', title: 'Speak naturally', desc: 'Just talk — the AI gathers all required details.' },
                    { icon: '📋', title: 'Instant intake', desc: 'Your information is captured and submitted in real time.' },
                    { icon: '🔒', title: 'Secure & private', desc: 'All data is encrypted and HIPAA-compliant.' },
                  ].map(({ icon, title, desc }) => (
                    <div
                      key={title}
                      className="flex items-start gap-3 p-4 bg-white/40 backdrop-blur-sm rounded-xl border border-white/60"
                    >
                      <span className="text-2xl mt-0.5">{icon}</span>
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            </div>

            {/* Right Panel — ElevenLabs widget */}
            <div
              className="hidden lg:flex lg:w-1/2 flex-col justify-center elevenlabs-signup-container"
              style={{ height: 'calc(100vh - 4rem)', position: 'relative', zIndex: 10 }}
            >
              <div style={{ flex: 1, width: '100%', background: 'white', borderRadius: '1rem', overflow: 'hidden' }}>
                {widgetReady ? (
                  <elevenlabs-convai
                    agent-id={agentId}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      background: 'white',
                      border: 'none',
                      boxShadow: 'none',
                      position: 'relative',
                      zIndex: 10,
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-6 p-6">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg animate-pulse">
                        <span className="text-3xl">📋</span>
                      </div>
                      <div className="absolute inset-0 rounded-full border-2 border-blue-400/50 animate-ping" style={{ animationDuration: '1.5s' }} />
                      <div className="absolute inset-0 rounded-full border border-indigo-400/30 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
                    </div>
                    <div className="text-center space-y-2">
                      <h2 className="text-xl font-semibold text-gray-900">Connecting to Registration Agent</h2>
                      <p className="text-sm text-gray-500">{tenant?.name || 'Vikram Aura Hospitals'} · New Patient Intake</p>
                    </div>
                    <div className="w-48 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 via-indigo-600 to-blue-500 rounded-full"
                        style={{ width: '40%', animation: 'loadingBar 1.5s ease-in-out infinite' }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <p className="mt-4 text-sm text-center text-gray-600">
                Already have an account?{' '}
                <a href="/login" className="text-blue-600 font-medium hover:underline">Sign in</a>
              </p>
            </div>

            {/* Mobile widget (below form) */}
            <div className="lg:hidden w-full">
              <div className="w-full bg-white rounded-2xl shadow-xl overflow-hidden elevenlabs-signup-container" style={{ height: '75vh', position: 'relative', zIndex: 10 }}>
                {widgetReady ? (
                  <elevenlabs-convai
                    agent-id={agentId}
                    style={{ width: '100%', height: '100%', display: 'block', border: 'none', boxShadow: 'none' }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 animate-pulse" />
                  </div>
                )}
              </div>
              <p className="mt-4 text-sm text-center text-gray-600">
                Already have an account?{' '}
                <a href="/login" className="text-blue-600 font-medium hover:underline">Sign in</a>
              </p>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default SignupAIAgent;
